import { skipToken } from '@reduxjs/toolkit/query'
import { isUndefined } from 'lodash-es'
import { useMemo } from 'react'
import { useFetchUserStatusQuery } from '@/api/user-api'
import { useFetchPostCollectionListQuery } from '@/api/workspaces-api'
import { usePermissions } from '@/hooks/use-permissions'
import {
  BasicService,
  CollectionService,
  ServiceCategory,
} from '@/types/mock-api-types'

interface Limit {
  numberOfCollections: number
  numberOfPosts: number
}

const UNLIMITED = Number.MAX_VALUE

type UsePostCollectionLimit = () => {
  limited: boolean
  limit: Limit
  isCollectionLimitReached: boolean
}

const usePostCollectionLimit: UsePostCollectionLimit = () => {
  const { data: userStatus } = useFetchUserStatusQuery()
  const { data: collections } = useFetchPostCollectionListQuery(
    userStatus?.currentWorkspaceId
      ? { stringify: true, page: 1, workspaceId: userStatus.currentWorkspaceId }
      : skipToken,
  )

  const { withInternalPermission } = usePermissions()

  const isCollectionService = (
    service?: BasicService,
  ): service is CollectionService =>
    service?.serviceCategory === ServiceCategory.Collection

  const foundCollectionService = (): CollectionService | undefined =>
    userStatus?.currentWorkspace.availableServices.find(isCollectionService)

  const collectionService = foundCollectionService()
  const limit: Limit = useMemo(() => {
    if (withInternalPermission) {
      return {
        numberOfCollections: UNLIMITED,
        numberOfPosts: UNLIMITED,
      }
    }

    if (collectionService !== undefined) {
      return {
        numberOfCollections: collectionService.memo.collectionLimit,
        numberOfPosts: collectionService.memo.collectionKolLimit,
      }
    }

    return {
      numberOfCollections: 0,
      numberOfPosts: 0,
    }
  }, [withInternalPermission, collectionService])

  const limited = limit.numberOfCollections !== UNLIMITED

  const isCollectionLimitReached =
    !limited || isUndefined(collections)
      ? false
      : collections.total >= limit.numberOfCollections

  return {
    limited,
    limit,
    isCollectionLimitReached,
  }
}

export default usePostCollectionLimit
